import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import NavBar from "./NavBar";
import Footer from "./Footer";
import { useCart } from "./CartContext";
import AddressForm, { getAddressErrors } from "./AddressForm";
import CheckoutSummary, { CheckoutTotals } from "./CheckoutSummary";
import countryCodes from "../assets/countryCodes";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
const PENDING_CHECKOUT_KEY = "slowComicsPendingCheckout";
const FULFILLED_PAYMENT_KEY = "slowComicsFulfilledPaymentIntent";
const INTERNATIONAL_PHONE_REQUIRED_MESSAGE =
  "A phone number is required for international orders so we can complete customs declarations.";

const getApiUrl = (path) => `${process.env.REACT_APP_API_URL}${path}`;

const getStripeReturnClientSecret = () =>
  new URLSearchParams(window.location.search).get("payment_intent_client_secret");

const getPendingCheckout = () => {
  try {
    const pendingCheckout = window.localStorage.getItem(PENDING_CHECKOUT_KEY);
    return pendingCheckout ? JSON.parse(pendingCheckout) : null;
  } catch (error) {
    console.error("Could not load pending checkout:", error);
    return null;
  }
};

const savePendingCheckout = (payload) => {
  window.localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(payload));
};

const clearPendingCheckout = () => {
  window.localStorage.removeItem(PENDING_CHECKOUT_KEY);
};

const getCartShape = (cart) => {
  const physicalBooks = cart.filter((item) => item.id === "nandi-book" && item.requiresShipping);
  const stickersBookmarks = cart.filter((item) => item.id !== "nandi-book" && item.type !== "Print");
  const prints = cart.filter((item) => item.type === "Print");
  const hasPhysicalItems = physicalBooks.length > 0 || stickersBookmarks.length > 0 || prints.length > 0;
  const hasDigitalItems = cart.some((item) => item.format === "digital" || !item.requiresShipping);
  const format =
    hasPhysicalItems
      ? "physical"
      : "digital";

  return {
    format,
    hasMixedFulfillment: hasPhysicalItems && hasDigitalItems,
    bookQuantity: physicalBooks.reduce((sum, item) => sum + item.quantity, 0),
    otherPhysicalQuantity: stickersBookmarks.reduce((sum, item) => sum + item.quantity, 0),
    printQuantity: prints.reduce((sum, item) => sum + item.quantity, 0),
  };
};

const getCartItemsForOrder = (cart) =>
  cart.map((item) => ({
    id: item.id,
    name: item.name,
    price: item.price,
    quantity: item.quantity,
    format: item.format,
    type: item.type,
    requiresShipping: item.requiresShipping,
  }));

const getOrderPayload = ({
  address,
  cart,
  format,
  isInternational,
  orderNote,
  orderQuantities,
  paymentProvider,
  shippingPrice,
  total,
  transactionId,
}) => ({
  name: address.name,
  email: address.email,
  ...orderQuantities,
  isInternational,
  total,
  shippingPrice: format === "physical" ? shippingPrice : 0,
  transactionId,
  paymentProvider,
  orderNote: orderNote?.trim() || "",
  cartItems: getCartItemsForOrder(cart),
  ...(format === "physical" && {
    address: {
      ...address,
      street2: address.apartment || "",
      country_code: countryCodes[address.country] || address.country_code || address.country,
    },
  }),
});

const mergeValidatedAddress = (currentAddress, validatedAddress = {}) => ({
  ...currentAddress,
  ...validatedAddress,
  apartment: validatedAddress.street2 || currentAddress.apartment,
  country_code: validatedAddress.country || validatedAddress.country_code || currentAddress.country_code,
  zip: validatedAddress.zip || validatedAddress.postal_code || currentAddress.zip,
});

const normalizeAddressForComparison = (address = {}) => ({
  street1: String(address.street1 || "").trim().toLowerCase(),
  street2: String(address.street2 || address.apartment || "").trim().toLowerCase(),
  city: String(address.city || "").trim().toLowerCase(),
  state: String(address.state || "").trim().toLowerCase(),
  zip: String(address.zip || address.postal_code || "").trim().toLowerCase(),
  country: String(address.country || address.country_code || "").trim().toLowerCase(),
});

const didShippoChangeAddress = (originalAddress, validatedAddress) => {
  const original = normalizeAddressForComparison(originalAddress);
  const validated = normalizeAddressForComparison(validatedAddress);

  return Object.keys(validated).some((key) => validated[key] && validated[key] !== original[key]);
};

const recordPurchase = ({ cart, currency, shippingPrice, total, transactionId }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "purchase",
    ecommerce: {
      transaction_id: transactionId,
      value: total,
      currency,
      shipping: shippingPrice || 0,
      items: cart.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
    },
  });
};

const getSuccessMessage = (format) =>
  format === "digital"
    ? "Payment successful! A download link has been sent to your email."
    : "Payment successful! Your order has been placed.";

const fulfillOrder = async (payload, format) => {
  const endpoint = format === "digital" ? "api/placeDigitalOrder" : "api/placeOrder";
  const response = await axios.post(getApiUrl(endpoint), payload);
  if (response.status === 202) {
    throw new Error(response.data?.error || 'Order fulfillment needs manual review.');
  }
};

const StripePaymentForm = ({
  address,
  cart,
  currency,
  format,
  isInternational,
  onOrderComplete,
  orderNote,
  orderQuantities,
  shippingPrice,
  total,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { clearCart } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const returnClientSecret = getStripeReturnClientSecret();
  const billingCountryCode = countryCodes[address.country] || address.country;
  const paymentElementOptions = {
    defaultValues: {
      billingDetails: {
        name: address.name,
        email: address.email,
        phone: address.phone,
        address: {
          line1: address.street1,
          line2: address.apartment || "",
          city: address.city,
          state: address.state,
          postal_code: address.zip,
          country: billingCountryCode,
        },
      },
    },
  };

  const finishSuccessfulPayment = useCallback(async (paymentIntentId) => {
    try {
      const payload = getOrderPayload({
        address,
        cart,
        format,
        isInternational,
        orderNote,
        orderQuantities,
        paymentProvider: "stripe",
        shippingPrice,
        total,
        transactionId: paymentIntentId,
      });

      await fulfillOrder(payload, format);
      recordPurchase({ cart, currency, shippingPrice, total, transactionId: paymentIntentId });
      window.localStorage.setItem(FULFILLED_PAYMENT_KEY, paymentIntentId);
      onOrderComplete();
      clearPendingCheckout();
      clearCart();
      alert(getSuccessMessage(format));
      history.push("/thank-you");
    } catch (error) {
      console.error("Order fulfillment error:", error);
      setPaymentError(
        "Your payment succeeded, but we could not finish the order automatically. Please contact Slow Comics with your payment ID."
      );
      setSubmitting(false);
    }
  }, [address, cart, clearCart, currency, format, history, isInternational, onOrderComplete, orderNote, orderQuantities, shippingPrice, total]);

  useEffect(() => {
    const completeRedirectPayment = async () => {
      if (!stripe || !returnClientSecret) return;

      setSubmitting(true);
      setPaymentError(null);

      const { paymentIntent, error } = await stripe.retrievePaymentIntent(returnClientSecret);

      if (error) {
        setPaymentError(error.message || "We could not verify the payment.");
        setSubmitting(false);
        return;
      }

      if (!paymentIntent) {
        setPaymentError("We could not find this payment.");
        setSubmitting(false);
        return;
      }

      if (window.localStorage.getItem(FULFILLED_PAYMENT_KEY) === paymentIntent.id) {
        history.replace("/thank-you");
        return;
      }

      if (paymentIntent.status === "succeeded") {
        await finishSuccessfulPayment(paymentIntent.id);
        return;
      }

      setPaymentError("Payment was not completed. You can try another payment method below.");
      setSubmitting(false);
    };

    completeRedirectPayment();
  }, [finishSuccessfulPayment, history, returnClientSecret, stripe]);

  const confirmAndFulfill = async () => {
    if (!stripe || !elements) return;

    setSubmitting(true);
    setPaymentError(null);
    savePendingCheckout({
      address,
      cart,
      currency,
      format,
      isInternational,
      orderNote,
      orderQuantities,
      shippingPrice,
      total,
    });

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout`,
      },
      redirect: "if_required",
    });

    if (error) {
      setPaymentError(error.message || "Payment could not be completed.");
      setSubmitting(false);
      return;
    }

    if (!paymentIntent) {
      return;
    }

    if (paymentIntent?.status !== "succeeded") {
      setPaymentError("Payment is still processing. Please wait a moment and try again.");
      setSubmitting(false);
      return;
    }

    await finishSuccessfulPayment(paymentIntent.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await confirmAndFulfill();
  };

  return (
    <form className="payment-form" onSubmit={handleSubmit}>
      <div className="express-checkout">
        <ExpressCheckoutElement onConfirm={confirmAndFulfill} />
      </div>

      <div className="checkout-divider">
        <span>or pay by card</span>
      </div>

      <PaymentElement options={paymentElementOptions} />

      {paymentError && <p className="error">{paymentError}</p>}

      <button className="confirm-button" type="submit" disabled={!stripe || submitting}>
        {submitting ? "Processing..." : `Pay $${total.toFixed(2)}`}
      </button>
    </form>
  );
};

export const PayPalPaymentSection = ({
  address,
  cart,
  currency,
  format,
  isInternational,
  onOrderComplete,
  orderNote,
  orderQuantities,
  shippingPrice,
  total,
}) => {
  const history = useHistory();
  const { clearCart } = useCart();
  const [paypalError, setPaypalError] = useState(null);

  const createOrder = (data, actions) =>
    actions.order.create({
      purchase_units: [
        {
          amount: {
            currency_code: currency,
            value: total.toFixed(2),
          },
          ...(format === "physical" && {
            shipping: {
              address: {
                address_line_1: address.street1,
                address_line_2: address.apartment || "",
                admin_area_2: address.city,
                admin_area_1: address.state,
                postal_code: address.zip,
                country_code: countryCodes[address.country] || address.country_code || address.country || "US",
              },
            },
          }),
        },
      ],
    });

  const onApprove = async (data, actions) => {
    setPaypalError(null);

    try {
      const order = await actions.order.capture();
      const transactionId = order.id;
      const payload = getOrderPayload({
        address,
        cart,
        format,
        isInternational,
        orderNote,
        orderQuantities,
        paymentProvider: "paypal",
        shippingPrice,
        total,
        transactionId,
      });

      await fulfillOrder(payload, format);
      recordPurchase({ cart, currency, shippingPrice, total, transactionId });
      onOrderComplete();
      clearPendingCheckout();
      clearCart();
      alert(getSuccessMessage(format));
      history.push("/thank-you");
    } catch (error) {
      console.error("PayPal order fulfillment error:", error);
      setPaypalError(
        "Your PayPal payment may have completed, but we could not finish the order automatically. Please contact Slow Comics with your PayPal transaction ID."
      );
    }
  };

  return (
    <div className="paypal-payment-section">
      <div className="checkout-divider">
        <span>or pay with PayPal</span>
      </div>

      <PayPalButtons
        style={{ layout: "vertical" }}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={(error) => {
          console.error("PayPal checkout error:", error);
          setPaypalError("PayPal checkout could not be completed. Please try again.");
        }}
        fundingSource="paypal"
      />

      {paypalError && <p className="error">{paypalError}</p>}
    </div>
  );
};

export const Checkout = () => {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);
  const pendingCheckout = getPendingCheckout();
  const stripeReturnClientSecret = getStripeReturnClientSecret();
  const { cart } = useCart();
  const history = useHistory();
  const [address, setAddress] = useState(pendingCheckout?.address || {
    name: "",
    email: "",
    street1: "",
    apartment: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    country_code: "US",
    phone: "",
  });
  const [orderNote, setOrderNote] = useState(pendingCheckout?.orderNote || "");
  const [shippingPrice, setShippingPrice] = useState(pendingCheckout?.shippingPrice ?? null);
  const [validatedShippingAddress, setValidatedShippingAddress] = useState(null);
  const [currency] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(stripeReturnClientSecret || null);
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const latestPaymentIntentKey = useRef(null);
  const latestShippingRequest = useRef(0);
  const checkoutFormRef = useRef(null);
  const [addressErrors, setAddressErrors] = useState({});

  const subtotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart]
  );
  const cartShape = useMemo(() => getCartShape(cart), [cart]);
  const total = cartShape.format === "physical" ? subtotal + (shippingPrice || 0) : subtotal;
  const countryCode = countryCodes[address.country] || address.country;
  const isInternational = countryCode !== "US";
  const hasRequiredInternationalPhone = !isInternational || Boolean(address.phone.trim());
  const canCreatePayment =
    cart.length > 0 &&
    address.name &&
    address.email &&
    hasRequiredInternationalPhone &&
    !cartShape.hasMixedFulfillment &&
    (cartShape.format === "digital" || shippingPrice !== null);
  const paymentIntentKey = useMemo(
    () =>
      JSON.stringify({
        cartItems: getCartItemsForOrder(cart),
        currency,
        email: address.email,
        format: cartShape.format,
        name: address.name,
        phone: address.phone,
        deliveryAddress: cartShape.format === "physical" ? address : null,
        shippingPrice: cartShape.format === "physical" ? shippingPrice : 0,
      }),
    [address, cart, cartShape.format, currency, shippingPrice]
  );

  useEffect(() => {
    if ((!cart || cart.length === 0) && !stripeReturnClientSecret && !orderComplete) {
      history.replace("/buy");
    }
  }, [cart, history, orderComplete, stripeReturnClientSecret]);

  useEffect(() => {
    if (stripeReturnClientSecret) return;
    setClientSecret(null);
  }, [cart, address.email, address.name, stripeReturnClientSecret]);

  const handleAddressChange = useCallback((updates) => {
    latestShippingRequest.current += 1;
    latestPaymentIntentKey.current = null;
    setAddress((previous) => {
      const next = { ...previous, ...updates };
      // An edited street/country must not retain a previous validation's house number.
      if (['street1', 'country', 'country_code'].some((key) => Object.prototype.hasOwnProperty.call(updates, key))) {
        delete next.streetNo;
      }
      return next;
    });
    setShippingPrice(null);
    setClientSecret(null);
    setLoading(false);
    setPaymentIntentLoading(false);
    setValidatedShippingAddress(null);
    setError(null);
    setAddressErrors((previous) => {
      const remaining = { ...previous };
      Object.keys(updates).forEach((key) => delete remaining[key]);
      return remaining;
    });
  }, []);

  useEffect(() => () => {
    latestShippingRequest.current += 1;
    latestPaymentIntentKey.current = null;
  }, []);

  const calculateShipping = async (event) => {
    event?.preventDefault();
    if (cartShape.format !== "physical") return;
    const requestId = ++latestShippingRequest.current;
    latestPaymentIntentKey.current = null;
    setError(null);
    setClientSecret(null);
    setPaymentIntentLoading(false);
    setShippingPrice(null);
    setValidatedShippingAddress(null);
    const fieldErrors = getAddressErrors(address);
    if (!address.name.trim()) fieldErrors.name = "Enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email.trim())) fieldErrors.email = "Enter a valid email address.";
    if (isInternational && !address.phone.trim()) fieldErrors.phone = INTERNATIONAL_PHONE_REQUIRED_MESSAGE;
    setAddressErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) {
      window.requestAnimationFrame(() => checkoutFormRef.current?.querySelector('[aria-invalid="true"]')?.focus());
      return;
    }

    setLoading(true);

    try {
      const submittedAddress = {
        ...address,
        street2: address.apartment || "",
        country_code: countryCodes[address.country] || address.country_code || address.country,
      };

      const response = await axios.post(getApiUrl("api/shippingCalculation"), {
        addressTo: submittedAddress,
        ...cartShape,
        isInternational,
      });

      // A quote for an older address must never enable payment for the edited address.
      if (latestShippingRequest.current !== requestId) return;

      if (response.data && response.data.totalShipping !== undefined) {
        if (response.data.validatedAddress) {
          setValidatedShippingAddress({
            ...response.data.validatedAddress,
            wasChanged: didShippoChangeAddress(submittedAddress, response.data.validatedAddress),
          });
          setAddress((currentAddress) =>
            mergeValidatedAddress(currentAddress, response.data.validatedAddress)
          );
        }
        setShippingPrice(Number(response.data.totalShipping));
      } else {
        setError("No shipping rates available.");
      }
    } catch (error) {
      if (latestShippingRequest.current !== requestId) return;
      console.error("Error calculating shipping:", error);
      setError(error.response?.data?.details || "There was an error calculating shipping. Please make sure your address is correct.");
    } finally {
      if (latestShippingRequest.current === requestId) setLoading(false);
    }
  };

  const createPaymentIntent = useCallback(async (requestKey) => {
    if (!canCreatePayment) return;

    setPaymentIntentLoading(true);
    setError(null);

    try {
      const response = await axios.post(getApiUrl("api/createPaymentIntent"), {
        name: address.name,
        email: address.email,
        currency,
        total,
        subtotal,
        shippingPrice: cartShape.format === "physical" ? shippingPrice : 0,
        format: cartShape.format,
        address: {
          ...address,
          street2: address.apartment || "",
          country_code: countryCodes[address.country] || address.country_code || address.country,
        },
        orderQuantities: cartShape,
        orderNote,
        cartItems: getCartItemsForOrder(cart),
      });

      if (latestPaymentIntentKey.current === requestKey) {
        setClientSecret(response.data.clientSecret);
      }
    } catch (error) {
      console.error("Payment setup error:", error);
      if (latestPaymentIntentKey.current === requestKey) {
        setError("We could not start checkout. Please try again in a moment.");
      }
    } finally {
      if (latestPaymentIntentKey.current === requestKey) {
        setPaymentIntentLoading(false);
      }
    }
  }, [address, canCreatePayment, cart, cartShape, currency, orderNote, shippingPrice, subtotal, total]);

  useEffect(() => {
    if (stripeReturnClientSecret || !canCreatePayment || clientSecret || paymentIntentLoading) return;
    latestPaymentIntentKey.current = paymentIntentKey;
    const paymentIntentTimer = window.setTimeout(() => {
      createPaymentIntent(paymentIntentKey);
    }, 600);

    return () => window.clearTimeout(paymentIntentTimer);
  }, [
    canCreatePayment,
    clientSecret,
    createPaymentIntent,
    paymentIntentKey,
    paymentIntentLoading,
    stripeReturnClientSecret,
  ]);

  return (
    <div>
      <NavBar />
      <div className="checkout-container">
        <h1 className="title">Checkout</h1>

        <div className="checkout-layout">
        <CheckoutSummary cart={cart} subtotal={subtotal} shippingPrice={shippingPrice} requiresShipping={cartShape.format === "physical"} />
        <div className="checkout-main">

        <form id="checkout-details" ref={checkoutFormRef} onSubmit={calculateShipping} noValidate>
        <div className="address-form">
          <label>
            Name
            <input
              type="text"
              name="name"
              autoComplete="shipping name"
              value={address.name}
              onChange={(e) => handleAddressChange({ name: e.target.value })}
              aria-invalid={Boolean(addressErrors.name)}
              aria-describedby={addressErrors.name ? "checkout-name-error" : undefined}
              required
            />
            {addressErrors.name && <span className="field-error" id="checkout-name-error">{addressErrors.name}</span>}
          </label>
          <label>
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              value={address.email}
              onChange={(e) => handleAddressChange({ email: e.target.value })}
              aria-invalid={Boolean(addressErrors.email)}
              aria-describedby={addressErrors.email ? "checkout-email-error" : undefined}
              required
            />
            {addressErrors.email && <span className="field-error" id="checkout-email-error">{addressErrors.email}</span>}
          </label>
          {cartShape.format === "physical" && (
            <label>
              <span id="checkout-phone-label">Phone Number <span className="phone-label-note">(needed for international orders)</span></span>
              <input
                type="tel"
                name="phone"
                autoComplete="shipping tel"
                value={address.phone}
                onChange={(e) => handleAddressChange({ phone: e.target.value })}
                aria-invalid={Boolean(addressErrors.phone)}
                aria-labelledby="checkout-phone-label"
                aria-describedby={addressErrors.phone ? "checkout-phone-error" : undefined}
                required={isInternational}
              />
              {addressErrors.phone && <span className="field-error" id="checkout-phone-error">{addressErrors.phone}</span>}
            </label>
          )}
        </div>

        {cartShape.format === "physical" && (
          <AddressForm address={address} onChange={handleAddressChange} errors={addressErrors} />
        )}

        <div className="address-form">
          <label>
            Order Notes
            <span className="field-description">
              If you would like your copy personalized, please put the name you would like it signed to here. You can also leave any other notes you have for us here.
            </span>
            <textarea
              name="orderNote"
              value={orderNote}
              onChange={(e) => setOrderNote(e.target.value)}
              placeholder="Personalization name or order notes"
              rows="4"
            />
          </label>
        </div>

        </form>

        {cartShape.format === "physical" ? (
          <>
            <button className="confirm-button" type="submit" form="checkout-details" disabled={loading}>
              {loading ? "Calculating..." : "Calculate Shipping"}
            </button>

            {shippingPrice !== null && (
              <div className="shipping-details">
                {validatedShippingAddress && (
                  <div className="validated-address">
                    <h3>Validated shipping address</h3>
                    {validatedShippingAddress.wasChanged && (
                      <p className="validated-address-note">
                        We standardized your address for carrier delivery.
                      </p>
                    )}
                    <p>
                      {validatedShippingAddress.street1}
                      {validatedShippingAddress.street2 && (
                        <>
                          <br />
                          {validatedShippingAddress.street2}
                        </>
                      )}
                      <br />
                      {validatedShippingAddress.city}, {validatedShippingAddress.state}{" "}
                      {validatedShippingAddress.zip || validatedShippingAddress.postal_code}
                      <br />
                      {validatedShippingAddress.country}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}

        {cartShape.format === "physical" && shippingPrice !== null && (
          <section className="checkout-summary checkout-mobile-payment-summary" aria-labelledby="mobile-order-total">
            <h2 id="mobile-order-total">Order total</h2>
            <CheckoutTotals subtotal={subtotal} shippingPrice={shippingPrice} requiresShipping />
          </section>
        )}

        {error && <p className="error">{error}</p>}

        {cartShape.hasMixedFulfillment && (
          <p className="error">
            Please check out shipped items and digital downloads separately.
          </p>
        )}

        {canCreatePayment && !clientSecret && paymentIntentLoading && (
          <div className="payment-loading">
            Preparing secure checkout...
          </div>
        )}

        {clientSecret && (
          <>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  variables: {
                    borderRadius: "5px",
                    colorPrimary: "#4a548a",
                  },
                },
              }}
            >
              <StripePaymentForm
                address={address}
                cart={cart}
                currency={currency}
                format={cartShape.format}
                isInternational={isInternational}
                onOrderComplete={() => setOrderComplete(true)}
                orderNote={orderNote}
                orderQuantities={cartShape}
                shippingPrice={shippingPrice || 0}
                total={total}
              />
            </Elements>

            {paypalClientId && (
              <PayPalScriptProvider options={{ "client-id": paypalClientId, currency }}>
                <PayPalPaymentSection
                  address={address}
                  cart={cart}
                  currency={currency}
                  format={cartShape.format}
                  isInternational={isInternational}
                  onOrderComplete={() => setOrderComplete(true)}
                  orderNote={orderNote}
                  orderQuantities={cartShape}
                  shippingPrice={shippingPrice || 0}
                  total={total}
                />
              </PayPalScriptProvider>
            )}
          </>
        )}
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;
